/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { type Registry as Services, service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { waitFor } from '@ember/test-waiters';
import { unloadRecord } from 'uplisting-frontend/utils/changeset';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { type GenericChangeset } from 'uplisting-frontend/services/repositories/-base';

interface IArgs {
  currency: string;
  index: number;
  booking: ModelRegistry['booking'];
  payment: ModelRegistry['payment'];
}

interface BookingPriceTablesPaymentsRowSignature {
  Element: HTMLTableRowElement;

  Args: IArgs;
}

export default class UiBookingPriceTablesPaymentsRowComponent extends Component<BookingPriceTablesPaymentsRowSignature> {
  @service intl!: Services['intl'];
  @service notifications!: Services['notifications'];

  @service('repositories/booking-refund-action')
  bookingRefundActionRepository!: Services['repositories/booking-refund-action'];

  @tracked isSaving = false;
  @tracked showRefundModal = false;

  @cached
  get changeset(): GenericChangeset<ModelRegistry['booking-refund-action']> {
    const record = this.bookingRefundActionRepository.createRecord({
      payment: this.payment,
      amount: this.payment.maxRefundAmount,
    });

    return this.bookingRefundActionRepository.buildChangeset(record);
  }

  @cached
  get currency(): string {
    return this.args.currency;
  }

  @cached
  get payment(): ModelRegistry['payment'] {
    return this.args.payment;
  }

  @cached
  get title(): string {
    if (this.payment.guestInvoice) {
      return this.intl.t('action_bookings_price.payments.guest_invoice.title', {
        id: this.payment.guestInvoice.externalId,
      });
    }

    return '';
  }

  @cached
  get sequence(): string {
    return 'I'.repeat(this.args.index + 1);
  }

  @action
  handleShowModal(): void {
    if (!this.payment.isRefundable) {
      return;
    }

    this.showRefundModal = true;
  }

  @action
  handleCloseModal(): void {
    if (!this.isSaving) {
      unloadRecord<ModelRegistry['booking-refund-action']>(this.changeset);
    }

    this.showRefundModal = false;
  }

  @action
  @waitFor
  async handleRefund(): Promise<void> {
    this.isSaving = true;

    try {
      await this.changeset.save();

      this.notifications.info(
        'action_bookings_price.refund_modal.success_notification',
      );

      await this.args.booking.reload();
    } catch {
      this.notifications.error();
    } finally {
      this.isSaving = false;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Booking::Price::Tables::Payments::Row': typeof UiBookingPriceTablesPaymentsRowComponent;
  }
}
